import { TradePanelRow } from 'store/services/trade/tradeApi';
import { ShippingBand, getShipDeadline, getShippingWindowState, parseWindowStart } from 'utils/shippingWindow';

// Progress for one rail row. Pure, and per row: nothing here is shared between
// rows or captured outside them.

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export interface RowProgress {
  /** The measured value, 0-100. For ready rows this is exactly what ShippingCountdown computes. */
  percentage: number;
  /**
   * Drawn bar width, 0-100. Differs from `percentage` only when a ready row is
   * overdue: the window has 0 percent remaining, and a zero-width bar could not
   * show the overdue colour at all.
   */
  fill: number;
  /** 'near' is a transit row inside its delivery window; 'idle' has nothing to measure */
  band: ShippingBand | 'idle' | 'near';
  label: string;
}

const formatRemaining = (ms: number): string => {
  const hours = Math.floor(ms / HOUR_MS);
  const minutes = Math.floor((ms % HOUR_MS) / (60 * 1000));
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

/**
 * Ready rows: the 72h shipping window, from the same util the per-package
 * ShippingCountdown reads, so both show the same percentage and band.
 */
export const getReadyProgress = (row: TradePanelRow, nowMs: number): RowProgress => {
  // No window start means there is no window: a trade-offer leg still waiting
  // on its funding leg, or a removed-item row. There is nothing to count down.
  // Never fall back to `initiated`: on a sequenced leg that is the accept time,
  // and it would read the leg as overdue the moment it opens.
  const startString = row.shipWindowStart;
  const startMs = startString ? parseWindowStart(startString) : null;
  if (row.awaitingFunding || startMs === null || isNaN(startMs)) {
    return { percentage: 0, fill: 0, band: 'idle', label: row.awaitingFunding ? 'Awaiting funding' : '' };
  }

  const state = getShippingWindowState(startMs, nowMs);
  return {
    percentage: state.percentage,
    fill: state.isExpired ? 100 : state.percentage,
    band: state.band,
    label: state.isExpired ? 'Overdue' : formatRemaining(getShipDeadline(startMs) - nowMs),
  };
};

/**
 * Transit rows: linear progress from `shipped` to `estimatedDeliveryBy`
 * (shipped + the estimate's maxDays; never derived from the initiated date).
 *
 * ONE clock decides the colour: the exact timestamps. transitEstimate.status is
 * deliberately not read for it, because the backend floors it to whole days and
 * it can disagree with estimatedDeliveryBy for up to a day at the boundary.
 *   - past estimatedDeliveryBy  -> overdue, red, labelled with whole days over
 *   - inside the delivery window (shipped + minDays onward) -> near, green
 *   - otherwise                 -> in progress, violet
 *   - no estimate               -> neutral grey, "No estimate yet"; never an
 *                                  invented overdue state
 */
export const getTransitProgress = (row: TradePanelRow, nowMs: number): RowProgress => {
  const shipped = row.shipped ? Date.parse(row.shipped) : NaN;
  const due = row.estimatedDeliveryBy ? Date.parse(row.estimatedDeliveryBy) : NaN;
  if (isNaN(shipped) || isNaN(due) || due <= shipped) {
    return { percentage: 0, fill: 0, band: 'idle', label: 'No estimate yet' };
  }

  if (nowMs > due) {
    // Floored, never under 1: a package an hour past its estimate is +1, not +0
    const overDays = Math.max(1, Math.floor((nowMs - due) / DAY_MS));
    return { percentage: 100, fill: 100, band: 'overdue', label: `+${overDays} ${overDays === 1 ? 'day' : 'days'}` };
  }

  const percentage = Math.max(0, Math.min(100, ((nowMs - shipped) / (due - shipped)) * 100));
  const minDays = row.transitEstimate?.minDays;
  const inWindow = typeof minDays === 'number' && nowMs >= shipped + minDays * DAY_MS;
  return {
    percentage,
    fill: percentage,
    band: inWindow ? 'near' : 'normal',
    label: row.transitEstimate?.label ?? '',
  };
};

export const getRowProgress = (row: TradePanelRow, nowMs: number): RowProgress =>
  row.status === 'ready' ? getReadyProgress(row, nowMs) : getTransitProgress(row, nowMs);
